import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Tag as TagIcon, ChevronRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogList = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag') || '';
  const searchQ = searchParams.get('q') || '';

  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchQ);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get(`${API}/blog/posts`, {
            params: { page: 1, page_size: 24, tag: activeTag || undefined, search: searchQ || undefined },
          }),
          axios.get(`${API}/blog/tags`),
        ]);
        setPosts(pRes.data?.items || []);
        setTags(tRes.data?.items || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeTag, searchQ]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput) next.set('q', searchInput); else next.delete('q');
    setSearchParams(next);
  };

  const setTag = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t) next.set('tag', t); else next.delete('tag');
    setSearchParams(next);
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  const baseUrl = isZh ? 'https://www.aligcus.com/zh/blog' : 'https://www.aligcus.com/blog';

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={isZh ? 'ALIGCUS 博客 | 加州 ADU 知识、法规与建造指南' : 'ALIGCUS Blog | California ADU Insights, Regulations & Building Guides'}
        description={isZh ? 'ALIGCUS 阿里建筑博客：分享加州 ADU 行业资讯、各市法规更新、户型选择、建造经验、客户故事。' : 'ALIGCUS Construction Blog: California ADU industry news, city regulation updates, floor plan tips, construction insights, and customer stories.'}
        canonical={baseUrl}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/blog' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/blog' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '博客' : 'Blog', url: baseUrl },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-gray-900 text-white py-20 pt-28">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">{isZh ? '博客中心' : 'ALIGCUS Blog'}</h1>
          <p className="text-lg text-gray-300 max-w-2xl">{isZh ? '加州 ADU 行业资讯、法规更新、建造经验、客户故事' : 'California ADU insights, regulation updates, construction tips & customer stories'}</p>
          <form onSubmit={submitSearch} className="mt-6 flex gap-2 max-w-md">
            <Input data-testid="blog-search-input" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={isZh ? '搜索文章...' : 'Search articles...'} className="bg-white text-gray-900" />
            <Button type="submit" className="bg-red-600 hover:bg-red-700"><Search className="w-4 h-4" /></Button>
          </form>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Tag pills */}
        {tags.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="blog-tag-bar">
            <button onClick={() => setTag('')} className={`px-4 py-1.5 rounded-full text-sm ${!activeTag ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {isZh ? '全部' : 'All'}
            </button>
            {tags.map(t => (
              <button key={t.tag} onClick={() => setTag(t.tag)} className={`px-4 py-1.5 rounded-full text-sm ${activeTag === t.tag ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                #{t.tag} <span className="opacity-60">({t.count})</span>
              </button>
            ))}
          </div>
        )}

        {/* Posts grid */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">{isZh ? '加载中...' : 'Loading...'}</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="blog-empty">
            {searchQ || activeTag ? (isZh ? '没有匹配的文章' : 'No matching articles') : (isZh ? '即将推出，敬请期待' : 'Coming soon — stay tuned')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map(p => {
              const title = isZh ? (p.title_zh || p.title_en) : (p.title_en || p.title_zh);
              const excerpt = isZh ? (p.excerpt_zh || p.excerpt_en) : (p.excerpt_en || p.excerpt_zh);
              const linkBase = isZh ? '/zh/blog' : '/blog';
              return (
                <Link key={p.id} to={`${linkBase}/${p.slug}`} className="group bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-100 transition overflow-hidden flex flex-col" data-testid={`blog-card-${p.slug}`}>
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                      <img src={p.cover_image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    {(p.tags || []).slice(0, 2).map(t => (
                      <span key={t} className="text-xs text-red-600 font-medium mb-2">#{t}</span>
                    )).slice(0, 1)}
                    <h2 className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-red-600 transition">{title}</h2>
                    {excerpt && <p className="text-sm text-gray-600 line-clamp-3 mb-3 flex-1">{excerpt}</p>}
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(p.published_at)}</span>
                      {p.read_time_min ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.read_time_min} min</span> : null}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogList;
